// The "New" view's rules (2026-10-08), pure so they're unit-testable: which
// template scope a From gives, the signature swap when the From changes, and
// when Send is enabled. NewEmailView.jsx is the thin JSX around them; the
// server (services/new-email.js) re-checks everything before sending.
import { isSignatureOnlyReply, findUnresolvedPlaceholders } from '@cxhub/shared'
import { parseCcText } from './emailCc.js'
import { isSingleValidEmail } from './suggestionActionInputs.js'
import { splitTopLevelHtmlBlocks, findSignatureBlockStart, signatureCompareText, htmlBlockText, withBlankLineAboveSignature, onlyBlankAbove } from './templateInsert.js'
import { toEditableHtml } from './richTextConversion.js'

export const SELECT_FROM_MESSAGE = 'Select a From address to see appropriate templates'

// The template scope for a From option ({ account, team } from GET
// /api/m365/new-email/options), the same scope a reply on that account's own
// contacts uses: service@1aauto.com -> 1A Website only, service@am-autoparts.com
// -> AM Website only. No From, no scope: the dialog shows SELECT_FROM_MESSAGE.
export function newEmailTemplateScope(option) {
  if (!option?.account) return null
  return { account: option.account, trqClientCode: null, team: option.team ?? 'customer_service' }
}

// The signature as the box shows it: the template with the agent's first name.
export function resolvedSignature(signatureTemplate, firstName) {
  if (!signatureTemplate) return ''
  return firstName ? signatureTemplate.replaceAll('[AGENT_FIRST_NAME]', firstName) : signatureTemplate
}

// The body after the From changes: the old signature (the trailing blocks
// that match it) is replaced by the new one, and everything above it is left
// alone. A blank body, or one holding only the seeded blank line above the
// old signature, becomes one blank line above the new signature (2026-10-12):
// never two. A body whose signature was edited away keeps its text and gets
// the new signature at the end.
export function swapSignature(body, oldSignature, newSignature) {
  const next = newSignature ?? ''
  if (!signatureCompareText(body)) return withBlankLineAboveSignature(next)
  const blocks = splitTopLevelHtmlBlocks(toEditableHtml(body))
  const texts = blocks.map(htmlBlockText)
  const at = oldSignature ? findSignatureBlockStart(texts, [oldSignature]) : -1
  if (at >= 0 && onlyBlankAbove(texts, at)) return withBlankLineAboveSignature(next)
  return `${(at < 0 ? blocks : blocks.slice(0, at)).join('')}${next}`
}

// Whether Send is enabled, and the first reason it isn't: a From, a single
// valid To, valid CCs, a Subject, a body that is more than the signature, and
// no INSERT* placeholder left.
export function newEmailSendState({ from, to, ccText = '', subject, body, signature = '' } = {}) {
  const cc = parseCcText(ccText)
  const placeholders = findUnresolvedPlaceholders(body ?? '')
  let reason = null
  if (!from) reason = 'Choose a From address.'
  else if (!isSingleValidEmail(to ?? '')) reason = 'Enter one valid To address.'
  else if (cc.invalid.length) reason = `Not an email address: ${cc.invalid.join(', ')}`
  else if (!String(subject ?? '').trim()) reason = 'Enter a subject.'
  else if (isSignatureOnlyReply(body ?? '', signature)) reason = 'Write the email: the body is empty or only the signature.'
  else if (placeholders.length) reason = `Fill in ${placeholders.join(', ')} before sending.`
  return { canSend: reason === null, reason, ccAddresses: cc.addresses }
}

// Whether leaving the view would lose something the agent did: any field
// typed, an attachment, or a body that is more than the seeded signature.
// Choosing a From alone loses nothing.
export function isNewEmailDirty({ to, ccText, subject, body, attachments = [], signature = '' } = {}) {
  if (String(to ?? '').trim() || String(ccText ?? '').trim() || String(subject ?? '').trim() || attachments.length) return true
  if (!isSignatureOnlyReply(body ?? '', signature)) return true
  return false
}
